import { useTranslation } from "react-i18next";
import { z } from "zod";
import { regexIsPhoneNumber, regexNonEmptyString } from "../../common/regex";
import { validatePassword } from "../../common/utils";

/**
 * Defines the payload of a sign in request.
 */
export type SignInRequest = {
	username: string;
	password: string;
	tosAccepted: boolean;
};

/**
 * Defines the payload of a "select workspace" request (post sig in).
 */
export type SelectWorkspacePayload = {
	selected: string;
};

/**
 * Defines the payload required to change users password.
 */
export type UpdatePasswordPayload = {
	oldPassword: string;
	password: string;
	passwordConfirm: string;
};

/**
 * Defines the payload required to update current user registry.
 */
export type UpdateCurrentAccountRegistryPayload = {
	firstName: string;
	lastName: string;
	telephone: string;
	preferredLanguage?: string;
};

/**
 * Defines the payload required to initiate user account recovery workflow.
 */
export type InitiateRecoverPasswordPayload = {
	email: string;
};

export type CompleteRecoverPasswordPayload = {
	token?: string;
	password: string;
	recoveryCode: string;
	passwordConfirm: string;
};

/**
 * Returns validation schemas used to validate auth payloads.
 */
export const useAuthValidationSchemas = () => {
	const { t } = useTranslation("validation");

	const UpdateCurrentAccountSchema = z.object({
		firstName: z.string().regex(regexNonEmptyString, t("common/required")),
		lastName: z.string().regex(regexNonEmptyString, t("common/required")),
		telephone: z.string().regex(regexIsPhoneNumber, t("telephone/wrong format")),
		preferredLanguage: z.string().optional(),
	});

	const InitiateRecoverPasswordSchema = z
		.object({
			email: z.string().regex(regexNonEmptyString, t("common/required")).email(t("common/invalid email")),
		})
		.refine((data) => data.email !== "");

	const CompleteRecoverPasswordSchema = z
		.object({
			recoveryCode: z.string().regex(regexNonEmptyString, t("common/required")),
			password: z.string().regex(regexNonEmptyString, t("common/required")),
			passwordConfirm: z.string().regex(regexNonEmptyString, t("common/required")),
			token: z.string().optional(),
		})
		.superRefine((data, ctx) => {
			const issues = validatePassword(data.password, data.passwordConfirm, t);

			for (const issue of issues) {
				ctx.addIssue(issue);
			}
		});

	const UpdatePasswordSchema = z
		.object({
			oldPassword: z.string(),
			password: z.string(),
			passwordConfirm: z.string(),
		})
		.superRefine((data, ctx) => {
			const issues = validatePassword(data.password, data.passwordConfirm, t);

			for (const issue of issues) {
				ctx.addIssue(issue);
			}
		})
		.refine((data) => data.password !== "" && data.passwordConfirm !== "" && data.oldPassword !== "");

	return {
		UpdatePasswordSchema,
		InitiateRecoverPasswordSchema,
		CompleteRecoverPasswordSchema,
		UpdateCurrentAccountSchema,
	};
};
